import type { HttpClient } from "@/shared/api/http";
import {
  loadQueueFromStorage,
  saveQueueToStorage,
} from "@/offline/queue/queueStorage";
import { executeOfflineCommand } from "@/offline/sync/commandHandlers";
import type {
  EnqueueResult,
  FlushResult,
  OfflineCommand,
  SyncStatusSnapshot,
} from "@/offline/types";
import { ONLINE_ONLY_ACTIONS } from "@/offline/types";
import { platform } from "@/platform";

/** In-memory mirror — persisted to localStorage (BL-S4-014 / FE-12). */
let queue: OfflineCommand[] = loadQueueFromStorage();

function persist(): void {
  saveQueueToStorage(queue);
}

function refreshFromStorage(): void {
  queue = loadQueueFromStorage();
}

export function listOfflineCommands(): OfflineCommand[] {
  refreshFromStorage();
  return [...queue];
}

export function getSyncStatusSnapshot(): SyncStatusSnapshot {
  refreshFromStorage();
  const pendingCount = queue.filter((c) => c.status === "Pending").length;
  const failedCount = queue.filter((c) => c.status === "Failed").length;
  const synced = queue.filter((c) => c.status === "Synced");
  const lastSyncedAt =
    synced
      .map((c) => c.syncedAt)
      .filter((v): v is string => Boolean(v))
      .sort()
      .at(-1) ?? null;

  return {
    online: platform.network.isOnline(),
    pendingCount,
    failedCount,
    syncedCount: synced.length,
    lastSyncedAt,
  };
}

export function enqueueOfflineCommand(
  cmd: Omit<OfflineCommand, "id" | "status" | "createdAt">,
): EnqueueResult {
  refreshFromStorage();
  if (
    ONLINE_ONLY_ACTIONS.includes(
      cmd.actionType as (typeof ONLINE_ONLY_ACTIONS)[number],
    )
  ) {
    return {
      ok: false,
      message: "Hành động này chỉ thực hiện khi online.",
    };
  }

  const entry: OfflineCommand = {
    ...cmd,
    id: crypto.randomUUID(),
    status: "Pending",
    createdAt: new Date().toISOString(),
  };
  queue.push(entry);
  persist();
  return { ok: true, id: entry.id };
}

/** Sync flush — requires HttpClient from auth context. */
export async function flushOfflineQueue(http: HttpClient): Promise<FlushResult> {
  refreshFromStorage();
  if (!platform.network.isOnline()) {
    return {
      ok: false,
      message: "Đang offline — không sync được.",
      synced: 0,
      failed: 0,
    };
  }

  const pending = queue.filter((c) => c.status === "Pending");
  if (pending.length === 0) {
    return { ok: true, synced: 0, failed: 0 };
  }

  let synced = 0;
  let failed = 0;

  for (const cmd of pending) {
    const result = await executeOfflineCommand(http, cmd);
    const idx = queue.findIndex((c) => c.id === cmd.id);
    if (idx < 0) continue;

    if (result.ok) {
      const current = queue[idx]!;
      queue[idx] = {
        ...current,
        status: "Synced",
        syncedAt: new Date().toISOString(),
        errorMessage: undefined,
      };
      synced += 1;
    } else {
      const current = queue[idx]!;
      queue[idx] = {
        ...current,
        status: "Failed",
        errorMessage: result.errorMessage ?? "Sync thất bại",
      };
      failed += 1;
    }
  }

  persist();
  return {
    ok: failed === 0,
    message: failed > 0 ? `${failed} lệnh thất bại` : undefined,
    synced,
    failed,
  };
}

/** @deprecated use flushOfflineQueue(http) — kept for callers without http during transition */
export function flushOfflineQueueLegacy(): FlushResult {
  if (!platform.network.isOnline()) {
    return {
      ok: false,
      message: "Đang offline — không sync được.",
      synced: 0,
      failed: 0,
    };
  }
  refreshFromStorage();
  const pending = queue.filter((c) => c.status === "Pending");
  if (pending.length === 0) {
    return { ok: true, synced: 0, failed: 0 };
  }
  return {
    ok: false,
    message: "Cần đăng nhập để sync hàng đợi.",
    synced: 0,
    failed: pending.length,
  };
}

export function retryFailedCommands(): number {
  refreshFromStorage();
  let count = 0;
  queue = queue.map((c) => {
    if (c.status !== "Failed") return c;
    count += 1;
    return { ...c, status: "Pending", errorMessage: undefined };
  });
  if (count > 0) persist();
  return count;
}

export function clearSyncedCommands(): number {
  refreshFromStorage();
  const before = queue.length;
  queue = queue.filter((c) => c.status !== "Synced");
  const removed = before - queue.length;
  if (removed > 0) persist();
  return removed;
}
